import {
  Avatar,
  Box,
  Button,
  Link,
  Popover,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DBRequest } from "../../api/dbRequest";
import { isTenantRequiredError, TENANT_REQUIRED_MESSAGE } from "../../api/creditInfo";
import keycloak from "../../security/keycloak";
import { tokens } from "../../theme/theme";

export const Person = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState(null);
  const [creditInfo, setCreditInfo] = useState({
    credit_balance: 0,
    monthly_credit_balance: 0,
    purchased_credit_balance: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleResponse = (response) => {
    setCreditInfo({
      credit_balance: response.credit_balance || 0,
      monthly_credit_balance: response.monthly_credit_balance || 0,
      purchased_credit_balance: response.purchased_credit_balance || 0,
    });
  };

  const handleError = (err) => {
    console.error("Failed to fetch credit information:", err);
    setError(err);
  };

  useEffect(() => {
    DBRequest({
      path: "credit_info",
      method: "get",
      onResponse: handleResponse,
      onLoading: setIsLoading,
      onError: handleError,
    });
  }, []);

  const open = Boolean(anchorEl);

  if (!keycloak.authenticated) {
    return null;
  }

  const userEmail = keycloak.tokenParsed.email || "N/A";
  const userName = keycloak.tokenParsed.name || "User";
  const userInitials = userEmail.substring(0, 2).toUpperCase();
  const realm = process.env.REACT_APP_KEYCLOAK_REALM;
  const accountUrl = `https://sso.swis-gmbh.de/auth/realms/${realm}/account/`;
  const totalCredits = creditInfo.credit_balance || 0;
  const tenantRequired = isTenantRequiredError(error);

  return (
    <Box>
      <Avatar
        sx={{
          bgcolor: colors.greenAccent[500],
          width: 50,
          height: 50,
          cursor: "pointer",
          fontSize: "1.5rem",
        }}
        onClick={handleClick}
      >
        {userInitials}
      </Avatar>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        className="mt-4"
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box
          className="flex flex-col gap-2 items-center p-4"
          sx={{ minWidth: "280px" }}
        >
          <Typography variant="h4" textAlign="center">
            {userName}
          </Typography>
          <Typography variant="h5" sx={{ color: colors.contrast[700] }}>
            {userEmail}
          </Typography>

          <Box className="flex flex-col justify-center w-full my-4">
            <Typography
              variant="h6"
              textAlign="center"
              sx={{ color: colors.contrast[700] }}
            >
              {t("Available credits")}
            </Typography>
            <Typography variant={tenantRequired ? "body1" : "h2"} textAlign="center">
              {isLoading
                ? "..."
                : error
                ? (tenantRequired ? TENANT_REQUIRED_MESSAGE : t("Error"))
                : totalCredits.toLocaleString("de-DE")}
            </Typography>
            {!isLoading && !error && (
              <Box className="flex flex-row justify-center gap-4 mt-2">
                <Typography variant="h5">
                  {t("Monthly")}:{" "}
                  {creditInfo.monthly_credit_balance.toLocaleString("de-DE")}
                </Typography>
                <Typography variant="h5">
                  {t("Purchased")}:{" "}
                  {creditInfo.purchased_credit_balance.toLocaleString("de-DE")}
                </Typography>
              </Box>
            )}
          </Box>

          <Button
            fullWidth
            variant="contained"
            color="secondary"
            onClick={() => {
              handleClose();
              navigate("/billing");
            }}
          >
            {t("Buy credits / subscription")}
          </Button>

          <Box className="flex flex-row gap-4 w-full justify-between mt-2">
            <Link target="_blank" href={accountUrl} style={{ textDecoration: "none" }}>
              <Button variant="contained">{t("Settings")}</Button>
            </Link>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => keycloak.logout()}
            >
              {t("Logout")}
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};
